import { useRef, useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Save, MapPin } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { Field } from "./ImageField";
import { createTileMap, pctToLatLng, latLngToPct, pinIcon } from "../../lib/mapTiles";

const RanchMap = ({ items, onMove, onSelect }) => {
  const divRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const markersRef = useRef({});
  const draggingIdRef = useRef(null);
  const cbRef = useRef({ onMove, onSelect });
  cbRef.current = { onMove, onSelect };

  useEffect(() => {
    if (mapRef.current || !divRef.current) return;
    const { map } = createTileMap(divRef.current, { zoomControl: true });
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 100);
    return () => { map.remove(); mapRef.current = null; markersRef.current = {}; };
  }, []);

  useEffect(() => {
    const map = mapRef.current, layer = layerRef.current;
    if (!map || !layer) return;
    const seen = new Set();
    items.forEach((b) => {
      seen.add(b.id);
      let m = markersRef.current[b.id];
      const latlng = pctToLatLng(map, b.x, b.y);
      if (!m) {
        m = L.marker(latlng, { icon: pinIcon(true), draggable: true, autoPan: true });
        m.bindTooltip(b.name || "Ranch", { direction: "top", offset: [0, -26] });
        m.on("click", () => cbRef.current.onSelect(b.id));
        m.on("dragstart", () => { draggingIdRef.current = b.id; });
        m.on("dragend", () => { draggingIdRef.current = null; const p = latLngToPct(map, m.getLatLng()); cbRef.current.onMove(b.id, p.x, p.y); });
        m.addTo(layer);
        markersRef.current[b.id] = m;
      } else {
        if (draggingIdRef.current !== b.id) {
          const cur = m.getLatLng();
          if (Math.abs(cur.lat - latlng.lat) > 1e-6 || Math.abs(cur.lng - latlng.lng) > 1e-6) m.setLatLng(latlng);
        }
        m.setTooltipContent(b.name || "Ranch");
      }
    });
    Object.keys(markersRef.current).forEach((id) => {
      if (!seen.has(id)) { layer.removeLayer(markersRef.current[id]); delete markersRef.current[id]; }
    });
  }, [items]);

  return <div ref={divRef} data-testid="admin-ranch-map" className="w-full aspect-[2000/1483] bg-[#141210]" style={{ zIndex: 0 }} />;
};

export const RanchesEditor = () => {
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try { const { data } = await adminApi.get("/ranches"); setItems(data); } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = (id, key, val) => setItems((p) => p.map((r) => (r.id === id ? { ...r, [key]: val } : r)));

  const add = async () => {
    try { await adminApi.post("/ranches", { name: "New Ranch", house_id: "", coords: "", x: 50, y: 50 }); await load(); toast.success("Ranch added"); }
    catch { toast.error("Failed to add"); }
  };
  const save = async (r) => {
    try { await adminApi.put(`/ranches/${r.id}`, r); toast.success("Saved"); }
    catch { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    try { await adminApi.delete(`/ranches/${id}`); setItems((p) => p.filter((r) => r.id !== id)); }
    catch { toast.error("Delete failed"); }
  };
  const onMove = (id, x, y) => {
    setItems((p) => p.map((r) => (r.id === id ? { ...r, x, y } : r)));
    const r = items.find((i) => i.id === id);
    if (r) save({ ...r, x, y });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <p className="text-[#8F877C] text-sm">Add ranch markers for the Staff ops map. Drag a pin to place it (auto-saves), then fill in the House ID and in-game coordinates staff will copy to teleport.</p>
        <button onClick={add} data-testid="ranch-add-btn" className="inline-flex shrink-0 items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal text-obsidian px-4 py-2.5 hover:bg-bone transition-colors"><Plus className="h-4 w-4" /> Add Ranch</button>
      </div>

      <div className="relative overflow-hidden border border-[#2A201A]">
        <RanchMap items={items} onMove={onMove} onSelect={() => {}} />
      </div>

      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.id} data-testid={`ranch-edit-${r.id}`} className="border border-[#2A201A] bg-surface/40 p-4 space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Ranch name" value={r.name} onChange={(v) => patch(r.id, "name", v)} />
              <Field label="House ID" value={r.house_id} onChange={(v) => patch(r.id, "house_id", v)} />
            </div>
            <Field label="Coordinates (staff copy this)" value={r.coords} onChange={(v) => patch(r.id, "coords", v)} placeholder="-1234.5, 678.9, 42.1" />
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10px] text-[#8F877C] flex items-center gap-1"><MapPin className="h-3 w-3" /> x:{r.x} y:{r.y}</span>
              <button onClick={() => save(r)} className="ml-auto inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal/90 text-obsidian px-4 py-2 hover:bg-teal"><Save className="h-4 w-4" /> Save</button>
              <button onClick={() => remove(r.id)} className="p-2 border border-[#2A201A] text-[#8F877C] hover:border-destructive hover:text-destructive transition-colors"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="font-mono text-xs uppercase tracking-widest text-[#5c554c] py-6">No ranches yet. Click “Add Ranch”.</p>}
      </div>
    </div>
  );
};
